/**
 * RequirementCard Component (DC-1060)
 * ============================================================================
 * Expandable card showing regulatory requirement details with citations.
 * Shows principle name, category badge, citation count. Expanded view shows
 * full requirement text, rationale, evidence required, and citations.
 *
 * @version 1.0.0
 * @created 2026-01-25
 * ============================================================================
 */

import React from 'react';
import type { RegulatoryRequirement, RegulatoryCitation } from '../../types/evidence';
import './requirement-card.css';

// ============================================================================
// Types
// ============================================================================

export interface RequirementCardProps {
  /** The requirement to display */
  requirement: RegulatoryRequirement;
  /** Whether the card is expanded */
  expanded?: boolean;
  /** Expanded detail data (fetched separately) */
  expandedDetail?: RegulatoryRequirement | null;
  /** Loading state for detail */
  loadingDetail?: boolean;
  /** Toggle expand/collapse handler */
  onToggleExpand: () => void;
  /** View citation handler */
  onViewCitation?: (citation: RegulatoryCitation) => void;
  /** Search query for highlighting */
  searchQuery?: string;
  /** Compact mode */
  compact?: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Highlight search terms in text
 */
function highlightText(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text;

  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);

  return parts.map((part, i) =>
    regex.test(part) ? (
      <mark key={i} className="requirement-card__highlight">
        {part}
      </mark>
    ) : (
      part
    )
  );
}

// ============================================================================
// Component
// ============================================================================

export const RequirementCard: React.FC<RequirementCardProps> = ({
  requirement,
  expanded = false,
  expandedDetail,
  loadingDetail = false,
  onToggleExpand,
  onViewCitation,
  searchQuery = '',
  compact = false,
}) => {
  // ============================================================================
  // Render Helpers
  // ============================================================================

  const getCategoryBadge = () => {
    if (requirement.category === 'alcoa') {
      return <span className="requirement-card__badge requirement-card__badge--alcoa">ALCOA+</span>;
    }
    return <span className="requirement-card__badge requirement-card__badge--fda">FDA Credibility</span>;
  };

  const renderCitation = (citation: RegulatoryCitation, index: number) => (
    <div key={index} className="requirement-card__citation">
      <div className="requirement-card__citation-header">
        <span className="requirement-card__citation-framework">{citation.framework}</span>
        <span className="requirement-card__citation-document">{citation.document}</span>
        <span className="requirement-card__citation-section">{citation.section}</span>
      </div>
      <blockquote className="requirement-card__citation-text">
        "{citation.text}"
      </blockquote>
      <div className="requirement-card__citation-footer">
        <span className="requirement-card__citation-relevance">{citation.relevance}</span>
        <div className="requirement-card__citation-actions">
          {onViewCitation && (
            <button
              className="requirement-card__citation-btn"
              onClick={(e) => {
                e.stopPropagation();
                onViewCitation(citation);
              }}
            >
              Details
            </button>
          )}
          <a
            href={citation.url}
            target="_blank"
            rel="noopener noreferrer"
            className="requirement-card__citation-link"
            onClick={(e) => e.stopPropagation()}
          >
            Source
          </a>
        </div>
      </div>
    </div>
  );

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className={`requirement-card ${expanded ? 'requirement-card--expanded' : ''} ${compact ? 'requirement-card--compact' : ''}`}>
      {/* Header (Always visible, clickable) */}
      <div
        className="requirement-card__header"
        onClick={onToggleExpand}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onToggleExpand()}
        aria-expanded={expanded}
      >
        <div className="requirement-card__title-row">
          <h4 className="requirement-card__title">
            {highlightText(requirement.principle, searchQuery)}
          </h4>
          {getCategoryBadge()}
        </div>

        <p className="requirement-card__summary">
          {highlightText(requirement.requirement, searchQuery)}
        </p>

        <div className="requirement-card__meta">
          <span className="requirement-card__citation-count">
            {requirement.citationCount} citation{requirement.citationCount !== 1 ? 's' : ''}
          </span>
          <span className="requirement-card__frameworks">
            {requirement.frameworks.join(', ')}
          </span>
          <span className="requirement-card__expand-icon">
            {expanded ? '▼' : '▶'}
          </span>
        </div>
      </div>

      {/* Expanded Detail */}
      {expanded && (
        <div className="requirement-card__detail">
          {loadingDetail ? (
            <div className="requirement-card__loading">
              <div className="requirement-card__loading-spinner" />
              <span>Loading details...</span>
            </div>
          ) : expandedDetail ? (
            <>
              {/* Rationale */}
              <div className="requirement-card__section">
                <h5 className="requirement-card__section-title">Rationale</h5>
                <p className="requirement-card__section-text">
                  {highlightText(expandedDetail.rationale, searchQuery)}
                </p>
              </div>

              {/* Evidence Required */}
              {expandedDetail.evidence_required && expandedDetail.evidence_required.length > 0 && (
                <div className="requirement-card__section">
                  <h5 className="requirement-card__section-title">Evidence Required</h5>
                  <ul className="requirement-card__evidence-list">
                    {expandedDetail.evidence_required.map((ev, i) => (
                      <li key={i}>{ev}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Evaluation Criteria */}
              {expandedDetail.evaluation_criteria && (
                <div className="requirement-card__section">
                  <h5 className="requirement-card__section-title">Evaluation Criteria</h5>
                  <p className="requirement-card__section-text">
                    {expandedDetail.evaluation_criteria}
                  </p>
                </div>
              )}

              {/* Thresholds */}
              <div className="requirement-card__section">
                <h5 className="requirement-card__section-title">Compliance Thresholds</h5>
                <div className="requirement-card__thresholds">
                  <span className="requirement-card__threshold requirement-card__threshold--compliant">
                    Compliant: ≥{expandedDetail.thresholds.compliant}%
                  </span>
                  <span className="requirement-card__threshold requirement-card__threshold--partial">
                    Partial: ≥{expandedDetail.thresholds.partial}%
                  </span>
                  <span className="requirement-card__threshold requirement-card__threshold--non">
                    Non-Compliant: &lt;{expandedDetail.thresholds.partial}%
                  </span>
                </div>
              </div>

              {/* Citations */}
              {expandedDetail.citations && expandedDetail.citations.length > 0 && (
                <div className="requirement-card__section requirement-card__section--citations">
                  <h5 className="requirement-card__section-title">
                    Regulatory Citations ({expandedDetail.citations.length})
                  </h5>
                  <div className="requirement-card__citations">
                    {expandedDetail.citations.map((citation, i) => renderCitation(citation, i))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="requirement-card__error">
              Failed to load requirement details.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RequirementCard;
